import { Storage } from 'aws-amplify';
import {
	colorString,
	similarity,
	quickSimilarity,
	byScore,
	selectedTactics,
} from './card-meta';
import { localFetch } from '../lib/local-fetch';
import { objectMap } from '../lib/util/object-map';

/**
 * @typedef {{
 * 	name: string;
 * 	tactics: Record<string, boolean>;
 * }} DeckCard
 */

/**
 * @typedef {{
 * 	name: string;
 * 	type: string;
 * 	cmc: number;
 * 	text: string;
 * }} Card
 */

/**
 * @typedef {Record<string, Card[]>} TacticGroups
 */

/**
 * @typedef {{
 * 	textTokensScore: number;
 * 	featureScore: number;
 * 	colorScore: number;
 * 	typeScore: number;
 * 	cmcScore: number;
 * }} Weights
 */

/**
 * @typedef {{
 * 	features: any[];
 * 	card: any;
 * 	weights: any;
 * 	score: number;
 * 	subScores: {
 * 		featureScore: number;
 * 		textTokensScore: number;
 * 		typeScore: number;
 * 		cmcScore: number;
 * 		colorScore: number;
 * 	};
 * }} SimilarCardHit
 */

/**
 * 
 */
class CardService {

	_manifest = undefined;
	_manifestData = undefined;
	_parts = [];
	_index = undefined;
	_indexData = undefined;
	_searchWorkerReady = undefined;

	constructor({manifest, index}) {
		this._manifest = manifest;
		this._index = index;
	}

	async getRemote(path) {
		const result = await Storage.get(
			`collection=cards/${path.replace('.json', '-gz.json')}`,
			{
				level: 'public',
				download: true
			}
		);
		return JSON.parse(await result.Body.text());
	}

	async getLocal(path) {
		const response = await fetch(`/data/${path}`);
		return response.json();
	}

	async getPath(path) {
		// just remote for now.
		return this.getRemote(path);
	}

	async manifest() {
		if (!this._manifestData) {
			this._manifestData = new Promise(resolve => {
				this.getPath(this._manifest).then(d => resolve(d));
			});
		}
		return this._manifestData;
	}

	async index() {
		if (this._index && !this._indexData) {
			this._indexData = new Promise(resolve => {
				this.getPath(this._index).then(d => resolve(d))
			})
		}
		return this._indexData;
	}

	async getPart(manifest, id) {
		if (!this._parts[id]) {
			this._parts[id] = new Promise(resolve => {
				const partUrl = `${manifest.name}/part-${id}-dense.json`;
				this.getPath(partUrl).then(data => {
					this.decorate(data);
					resolve({
						id,
						data,
						previousId: manifest[id - 1] ? id - 1: null,
						nextId: manifest[id + 1] ? id + 1 : null,
						keys: Object.keys(data).sort()
					});
				});
			});
		}
		return this._parts[id];
	}

	decorate(cardsData) {
		for (const [name, card] of Object.entries(cardsData)) {
			card.colors = colorString(card);
		}
	}

	async getAllParts() {
		const manifest = await this.manifest();

		const requests = [];
		for (const part of manifest.parts) {
			requests.push(this.getPart(manifest, part.part));
		}
		const parts = await Promise.all(requests);
		return parts.map(part => part.data);
	}

	async getAllCards() {
		const parts = await this.getAllParts();
		const cards = [];
		for (const part of parts) {
			for (const card of Object.values(part)) {
				cards.push(card)
			}
		}
		return cards;
	}

	async getPartByCard(card) {
		const normalizedName = card.toLowerCase();
		const manifest = await this.manifest();

		// TODO: If used for larger datasets, this needs to be binary searched.
		for (const part of manifest.parts) {
			if (normalizedName >= part.start && normalizedName <= part.end) {
				return await this.getPart(manifest, part.part)
			}
		}
	}

	/**
	 * 
	 * @param {string} name 
	 * @returns {Promise<Card>}
	 */
	async get(name) {
		const normalizedName = name.toLowerCase();
		const part = await this.getPartByCard(normalizedName);
		return part.data[normalizedName];
	};

	async getBatch(names) {
		return objectMap(names, name => this.get(name)).data
	};

	/*
	async find({
		text,
		type,
		cmc,
		colors,
		power,
		toughness,
	}) {
		const cards = await this.getAllCards();
		console.log('filter card sample', cards[0]);
		return cards.filter(card => {
			if (type && card.type.toLowerCase() !== type.toLowerCase()) {
				return false;
			}
			if (cmc && (card.cmc < cmc - 1 || card.cmc > cmc + 1) {
				return false;
			}
			if (typeof text === 'string') {
			} else if (typeof text === 'object') {
			} else if (Array.isArray(text)) {

			}
		});
	}
	*/

	async getIndex(query, after, page_size) {
		const results = [];
		let q = query.trim()

		const textQueryMatch = q.match(/text\s*=(.*)$/);
		if (textQueryMatch) {
			const textQuery = textQueryMatch[1];
			const conditions = textQuery
				.trim()
				.toLowerCase()
				.split(/&| and |\+/)
				.map(c => c.trim())
				.filter(Boolean)
			;
			const cards = await this.getAllCards();
			for (const card of cards) {
				if (
					conditions.length === 0
					|| !card.text
					|| (after && card.name.toLowerCase() < after.toLowerCase())
				) continue;

				const cardText = card
					.text
					.toLowerCase()
					.replaceAll(card.name.toLowerCase(), '{self}')
				;

				if (conditions.every(c => cardText.includes(c))) {
					results.push(card.name);
				}

				if (results.length >= page_size) {
					break;
				}
			}
		} else {
			const names = await this.index();
			for (const name of names) {
				if (name.includes((query || "").toLowerCase()) && name > (after || "").toLowerCase()) {
					results.push(name);
				}
	
				if (results.length >= page_size) {
					break;
				}
			}
		}
		
		return results;
	};

	/**
	 * @returns {Promise<void>}
	 */
	getSearchWorkerReady() {
		if (!this._searchWorkerReady) {
			this._searchWorkerReady = new Promise(resolve => {
				this.getAllCards().then(cards => {
					// we trim rulings out to save a little memory space, and
					// hopefully reduce serialization/transfer time.
					return cards.map(card => {
						const c = {...card};
						delete c.rulings;
						return c;
					})
				}).then(cards => {
					localFetch('/workers/card-search.js', 'populate', cards)
				}).then(resolve);
			});
		}
		return this._searchWorkerReady;
	}

	async getSimilar(card) {
		await this.getSearchWorkerReady();
		const results = await localFetch(
			'/workers/card-search.js',
			'getSimilar',
			{ card: await this.get(card.name), limit: 5 }
		).data;
		return results;
	}

	/**
	 * 
	 * @param {DeckCard[]} cards 
	 * @returns {TacticGroups}
	 */
	async groupedByTactic(cards) {
		const tactics = {};
		for (const card of cards) {
			for (const tactic of selectedTactics(card)) {
				tactics[tactic] = tactics[tactic] || [];
				tactics[tactic].push(await this.get(card.name));
			}
		}
		return tactics;
	};

	/**
	 * 
	 * @param {TacticGroups} cardGroups 
	 * @returns {Promise<Weights>}
	 */
	getWeights(cardGroups) {
		const weights = objectMap(cardGroups, async cards => localFetch(
			'/workers/card-search.js',
			'findSimilarities',
			cards
		).data);
		return weights.data;
	};

	/**
	 * 
	 * @param {TacticGroups} cardGroups 
	 * @param {Promise<Weights>} weights
	 * @returns {{ data: Promise<Record<string, SimilarCardHit[]>> }}
	 */
	getSimilarCardsForGroups(cardGroups, weights) {
		return objectMap(
			cardGroups,
			async (cards, tactic) => await this.getSimilarCardsForGroup(cards, weights[tactic])
		);
	}

	/**
	 * 
	 * @param {Card[]} cards
	 * @param {Weights} weights 
	 * @returns {{ data: Promise<SimilarCardHit[]> }}
	 */
	async getSimilarCardsForGroup(cards, weights) {
		await this.getSearchWorkerReady();
		return objectMap(
			Object.fromEntries(cards.map(card => [card.name, card])),
			card => localFetch(
				'/workers/card-search.js',
				'getSimilar',
				{ card, weights, limit: 20 }
			).data
		)
	}

	/**
	 * Given a list of cards (with their tactics), searches for similar cards.
	 * Results are grouped by tactic.
	 * 
	 * @param {DeckCard[]} cards 
	 * @returns 
	 */
	async getRecommendations(cards) {
		const grouped = await this.groupedByTactic(cards);
		const weights = await this.getWeights(grouped);
		return this.getSimilarCardsForGroups(grouped, weights);
	}

};

const Cards = new CardService({
	manifest: 'all-manifest-dense.json',
	index: 'names-dense.json'
});

/**
 * For playing with in the console.
 */
global.Cards = Cards;
global.Storage = Storage;

export default Cards;
